import React from "react";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from "@material-ui/icons/Description";
import ImageIcon from "@material-ui/icons/Image";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";

/** Selos da ocorrência (espelha OCCURRENCE_STATUSES do backend). */
export const STATUS_CONFIG = {
  em_andamento: { label: "Em andamento", color: "#1565C0" },
  pendencia_seguradora: { label: "Pendência com a seguradora", color: "#E65100" },
  pendencia_cliente_terceiro: { label: "Pendência com cliente/terceiro", color: "#F9A825" },
  resolvida: { label: "Resolvida", color: "#2E7D32" },
};

export const STATUS_KEYS = Object.keys(STATUS_CONFIG);

/** Valores legados: aberta -> em_andamento, fechada -> resolvida; desconhecido -> em_andamento. */
export function normalizeStatus(status) {
  if (status === "fechada") return "resolvida";
  if (status === "aberta") return "em_andamento";
  return Object.prototype.hasOwnProperty.call(STATUS_CONFIG, status) ? status : "em_andamento";
}

export const statusLabel = (status) => STATUS_CONFIG[normalizeStatus(status)].label;
export const statusColor = (status) => STATUS_CONFIG[normalizeStatus(status)].color;

/** ISO -> "dd/mm/aaaa" no fuso America/Sao_Paulo (nunca corta a string ISO). */
export function formatDateBR(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

/**
 * Mesmos tipos aceitos pelo servidor (config/uploadOccurrenceAttachment.ts).
 * O objeto está no formato que o react-dropzone (v14) espera em `accept`.
 */
export const ACCEPTED_ATTACHMENT_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};

export const MAX_FILES = 10;
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 24 * 1024 * 1024;
export const TOTAL_TOO_BIG_MESSAGE = "O total dos anexos passa de 24 MB. Envie em mais de um andamento.";
export const MAX_THIRD_PARTY_CHARS = 500;

/**
 * Validação no cliente, só para dar retorno imediato — quem decide de
 * verdade é o servidor (MIME + tamanho + limite de arquivos), que revalida
 * tudo de novo.
 */
export function validateAttachmentSelection(
  files,
  alreadyQueued = 0,
  alreadyBytes = 0,
  maxTotalBytes = Infinity
) {
  let totalBytes = alreadyBytes;
  const accepted = [];
  const rejected = [];
  const allowedMimeTypes = Object.keys(ACCEPTED_ATTACHMENT_TYPES);

  files.forEach((file) => {
    if (!allowedMimeTypes.includes(file.type)) {
      rejected.push({ name: file.name, reason: "Tipo de arquivo não permitido" });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      rejected.push({ name: file.name, reason: "Maior que 25 MB" });
      return;
    }
    if (accepted.length + alreadyQueued >= MAX_FILES) {
      rejected.push({ name: file.name, reason: `Limite de ${MAX_FILES} arquivos por ocorrência` });
      return;
    }
    if (totalBytes + file.size > maxTotalBytes) {
      rejected.push({ name: file.name, reason: TOTAL_TOO_BIG_MESSAGE });
      return;
    }
    totalBytes += file.size;
    accepted.push(file);
  });

  return { accepted, rejected };
}

/** Nome de arquivo para imagem colada: print_AAAAMMDD_HHMMSS.png */
export function pastedImageName(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `print_${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(
    date.getHours()
  )}${pad(date.getMinutes())}${pad(date.getSeconds())}.png`;
}

/** Imagem colada (Ctrl+V) vira File com nome print_AAAAMMDD_HHMMSS.png; outros arquivos passam iguais. */
export function namePastedFiles(files, date = new Date()) {
  return files.map((file, i) => {
    if (!file.type || !file.type.startsWith("image/")) return file;
    const ext = file.type === "image/png" ? "png" : file.type.split("/")[1].replace("jpeg", "jpg");
    const base = pastedImageName(new Date(date.getTime() + i * 1000)).replace(/\.png$/, `.${ext}`);
    return new File([file], base, { type: file.type });
  });
}

/** Id só de uso local (key de lista / remoção antes do envio). */
export function makeLocalFileId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function AttachmentIcon({ mimeType, ...props }) {
  if (mimeType === "application/pdf") return <PictureAsPdfIcon {...props} />;
  if (mimeType && mimeType.startsWith("image/")) return <ImageIcon {...props} />;
  if (
    mimeType === "application/msword" ||
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return <DescriptionIcon {...props} />;
  }
  return <InsertDriveFileIcon {...props} />;
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "2026-09-27T12:00:00.000Z" -> "27/09/2026 09:00". Sem libs extras. */
export function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/** Mensagem em português para erros de rede/limite; null = usar o toastError padrão. */
export function friendlyErrorMessage(err) {
  const status = err && err.response && err.response.status;
  if (status === 413) return "Os arquivos são grandes demais. Envie em mais de um andamento.";
  if (status === 429) return "Muitas tentativas em pouco tempo. Aguarde um instante e tente novamente.";
  return null;
}
